// Image to Shape Processing Functions
// This file contains all functions related to converting images to shape contours

// Dev mode flag - when enabled, previews will not be drawn
let devMode = false;
function setDevMode(enabled) {
  devMode = !!enabled;
}
function isDevMode() {
  return devMode;
}
if (typeof window !== 'undefined') {
  window.setDevMode = setDevMode;
  window.isDevMode = isDevMode;
}

function getDeviceProfile() {
  const defaults = {
    maxEdgeSamples: 1024,
    finalSampleCap: 512,
    edgeSpacingDivisor: 320,
    finalSampleDivisor: 200,
    dilationScale: 1.0,
  };
  if (typeof window === 'undefined') return defaults;
  const profile = window.deviceProfile || {};
  return {
    maxEdgeSamples: profile.maxEdgeSamples || defaults.maxEdgeSamples,
    finalSampleCap: profile.finalSampleCap || defaults.finalSampleCap,
    edgeSpacingDivisor: profile.edgeSpacingDivisor || defaults.edgeSpacingDivisor,
    finalSampleDivisor: profile.finalSampleDivisor || defaults.finalSampleDivisor,
    dilationScale: typeof profile.dilationScale === 'number' ? profile.dilationScale : defaults.dilationScale,
  };
}

function buildBinaryGrid(image, tolerance) {
  // Use naturalWidth/naturalHeight to ensure we get the actual image dimensions
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;

  if (width === 0 || height === 0) {
    console.error('Invalid image dimensions in buildBinaryGrid:', width, height);
    return { grid: new Uint8Array(0), width: 0, height: 0 };
  }

  // Validate image is fully loaded before processing
  if (!image.complete) {
    console.error('Image is not complete in buildBinaryGrid');
    return { grid: new Uint8Array(0), width: 0, height: 0 };
  }

  // Additional validation: ensure image is fully decoded and can be drawn to canvas
  // This prevents the square shape issue on some browsers where image.complete is true
  // but the image isn't fully decoded yet
  const testCanvas = document.createElement('canvas');
  testCanvas.width = width;
  testCanvas.height = height;
  const testCtx = testCanvas.getContext('2d');

  try {
    // Clear and draw to test canvas
    testCtx.clearRect(0, 0, width, height);
    testCtx.drawImage(image, 0, 0);

    // Test if we can read pixel data - this ensures the image was actually drawn
    const testData = testCtx.getImageData(0, 0, Math.min(10, width), Math.min(10, height));
    if (!testData || !testData.data || testData.data.length === 0) {
      console.error('Image pixel data not accessible - image may not be fully decoded');
      return { grid: new Uint8Array(0), width: 0, height: 0 };
    }

    // Check if the image actually has content (not all transparent)
    let hasVisibleContent = false;
    for (let i = 0; i < testData.data.length; i += 4) {
      if (testData.data[i + 3] > 0) { // Check alpha channel
        hasVisibleContent = true;
        break;
      }
    }

    if (!hasVisibleContent) {
      console.warn('Image appears to be completely transparent or not fully decoded');
      // Don't return empty grid immediately - let it proceed and handle in the main logic
    }

  } catch (e) {
    console.error('Error validating image for canvas drawing:', e.message);
    return { grid: new Uint8Array(0), width: 0, height: 0 };
  }

  const offCanvas = document.createElement('canvas');
  offCanvas.width = width;
  offCanvas.height = height;
  const ctx = offCanvas.getContext('2d');

  // Clear canvas first to ensure transparent pixels are truly transparent
  ctx.clearRect(0, 0, width, height);

  // Draw the image
  ctx.drawImage(image, 0, 0);
  
  // Get image data - this contains RGBA values
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const grid = new Uint8Array(width * height);

  // Only mark pixels as non-transparent if alpha channel is above tolerance
  // This ensures ONLY non-transparent pixels are used for shape generation
  let nonTransparentCount = 0;
  let totalPixels = data.length / 4;
  let maxAlpha = 0;
  let minAlpha = 255;

  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]; // Alpha channel (transparency)
    maxAlpha = Math.max(maxAlpha, alpha);
    minAlpha = Math.min(minAlpha, alpha);
    const isNonTransparent = alpha > tolerance;
    grid[i / 4] = isNonTransparent ? 1 : 0;
    if (isNonTransparent) {
      nonTransparentCount++;
    }
  }

  console.log(`Image analysis: ${width}x${height}, alpha range: ${minAlpha}-${maxAlpha}, tolerance: ${tolerance}, non-transparent pixels: ${nonTransparentCount}/${totalPixels}`);
  
  // Validate that we found non-transparent pixels
  if (nonTransparentCount === 0) {
    console.warn('No non-transparent pixels found in image (all pixels are transparent)');

    // Try a second attempt with a lower tolerance as a fallback
    // This handles cases where the tolerance might be too high due to browser differences
    if (tolerance > 0) {
      console.log('Retrying with lower tolerance (tolerance/2) to handle browser differences');
      let retryNonTransparentCount = 0;
      const retryTolerance = Math.max(0, tolerance / 2);

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        const isNonTransparent = alpha > retryTolerance;
        grid[i / 4] = isNonTransparent ? 1 : 0;
        if (isNonTransparent) {
          retryNonTransparentCount++;
        }
      }

      if (retryNonTransparentCount > 0) {
        console.log(`Retry successful: found ${retryNonTransparentCount} pixels with tolerance ${retryTolerance}`);
        nonTransparentCount = retryNonTransparentCount;
      } else {
        console.warn('Retry also failed - image appears to be completely transparent');
        return { grid: new Uint8Array(width * height), width, height };
      }
    } else {
      // Return empty grid instead of falling back to bounding box
      return { grid: new Uint8Array(width * height), width, height };
    }
  }
  
  console.log(`Found ${nonTransparentCount} non-transparent pixels out of ${width * height} total pixels`);
  return { grid, width, height };
}

function dilateBinary(grid, width, height, iterations) {
  if (iterations <= 0) return grid.slice();
  let src = grid.slice();
  let dst = new Uint8Array(width * height);
  for (let iter = 0; iter < iterations; iter++) {
    dst.set(src);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!src[y * width + x]) continue;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              dst[ny * width + nx] = 1;
            }
          }
        }
      }
    }
    const temp = src;
    src = dst;
    dst = temp;
  }
  return src;
}

function erodeBinary(grid, width, height, iterations) {
  if (iterations <= 0) return grid.slice();
  let src = grid.slice();
  let dst = new Uint8Array(width * height);
  for (let iter = 0; iter < iterations; iter++) {
    dst.set(src);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (!src[y * width + x]) continue;
        let allOn = true;
        for (let dy = -1; dy <= 1 && allOn; dy++) {
          for (let dx = -1; dx <= 1 && allOn; dx++) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || nx >= width || ny < 0 || ny >= height || !src[ny * width + nx]) {
              allOn = false;
            }
          }
        }
        dst[y * width + x] = allOn ? 1 : 0;
      }
    }
    const temp = src;
    src = dst;
    dst = temp;
  }
  return src;
}

function floodFill(grid, visited, width, height, startX, startY) {
  const pixels = [];
  const stack = [[startX, startY]];
  visited[startY * width + startX] = 1;
  while (stack.length) {
    const [x, y] = stack.pop();
    pixels.push({ x, y });
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < width && ny >= 0 && ny < height && grid[ny * width + nx] === 1 && !visited[ny * width + nx]) {
          visited[ny * width + nx] = 1;
          stack.push([nx, ny]);
        }
      }
    }
  }
  return pixels;
}

function drawLine(grid, width, height, x0, y0, x1, y1) {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  while (true) {
    if (x0 >= 0 && x0 < width && y0 >= 0 && y0 < height) {
      grid[y0 * width + x0] = 1;
      // Make thicker (3x3 around each point)
      for (let dy2 = -1; dy2 <= 1; dy2++) {
        for (let dx2 = -1; dx2 <= 1; dx2++) {
          const nx = x0 + dx2;
          const ny = y0 + dy2;
          if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
            grid[ny * width + nx] = 1;
          }
        }
      }
    }
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x0 += sx;
    }
    if (e2 < dx) {
      err += dx;
      y0 += sy;
    }
  }
}

function connectComponents(grid, width, height) {
  const visited = new Uint8Array(width * height);
  const components = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (grid[idx] === 1 && !visited[idx]) {
        const pixels = floodFill(grid, visited, width, height, x, y);
        if (pixels.length > 0) {
          let sumX = 0, sumY = 0;
          for (let p of pixels) {
            sumX += p.x;
            sumY += p.y;
          }
          const cx = sumX / pixels.length;
          const cy = sumY / pixels.length;
          components.push({ centroidX: cx, centroidY: cy });
        }
      }
    }
  }
  if (components.length <= 1) return;
  const edges = [];
  for (let i = 0; i < components.length; i++) {
    for (let j = i + 1; j < components.length; j++) {
      const dx = components[i].centroidX - components[j].centroidX;
      const dy = components[i].centroidY - components[j].centroidY;
      const dist = Math.hypot(dx, dy);
      edges.push({ i, j, dist });
    }
  }
  edges.sort((a, b) => a.dist - b.dist);
  let parent = [];
  for (let i = 0; i < components.length; i++) parent[i] = i;
  function find(p) {
    if (parent[p] !== p) parent[p] = find(parent[p]);
    return parent[p];
  }
  function union(a, b) {
    let pa = find(a), pb = find(b);
    if (pa !== pb) {
      parent[pa] = pb;
      return true;
    }
    return false;
  }
  for (let edge of edges) {
    if (union(edge.i, edge.j)) {
      const cx0 = Math.round(components[edge.i].centroidX);
      const cy0 = Math.round(components[edge.i].centroidY);
      const cx1 = Math.round(components[edge.j].centroidX);
      const cy1 = Math.round(components[edge.j].centroidY);
      drawLine(grid, width, height, cx0, cy0, cx1, cy1);
    }
  }
}

function traceContourFromGrid(grid, width, height) {
  // First, find all non-transparent pixels to ensure we capture everything
  const allNonTransparentPixels = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (grid[y * width + x] === 1) {
        allNonTransparentPixels.push({ x, y });
      }
    }
  }

  if (allNonTransparentPixels.length === 0) return [];

  // Find the bounding box of ALL non-transparent pixels
  // This ensures we capture everything, not just one connected component
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let p of allNonTransparentPixels) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  // Now find all edge pixels within this bounding box
  const allEdgePixels = [];
  const edgeSet = new Set();

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (grid[y * width + x] === 1) {
        // Check if this pixel has at least one transparent neighbor (making it an edge pixel)
        let isEdge = false;
        for (let dy = -1; dy <= 1 && !isEdge; dy++) {
          for (let dx = -1; dx <= 1 && !isEdge; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0) {
              isEdge = true;
              const key = `${x},${y}`;
              if (!edgeSet.has(key)) {
                allEdgePixels.push({ x, y });
                edgeSet.add(key);
              }
              break;
            }
          }
        }
      }
    }
  }

  if (allEdgePixels.length === 0) {
    // Only fallback to bounding box if we have valid non-transparent pixels
    // This should rarely happen if image loaded properly
    if (minX <= maxX && minY <= maxY && allNonTransparentPixels.length > 0) {
      console.warn('No edge pixels found, using bounding box fallback. This may indicate an image loading issue.');
      // Create a more detailed contour around the bounding box with more points
      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;
      const minSize = Math.min(boxWidth, boxHeight);
      
      // Only use bounding box if it's not essentially the entire image (which suggests loading issue)
      if (boxWidth < width * 0.9 && boxHeight < height * 0.9) {
        return [
          { x: minX, y: minY },
          { x: maxX, y: minY },
          { x: maxX, y: maxY },
          { x: minX, y: maxY }
        ];
      } else {
        console.error('Bounding box covers most of image - likely image loading issue. Returning empty contour.');
        return [];
      }
    }
    console.error('No non-transparent pixels found - image may not have loaded properly');
    return [];
  }

  // Find the top-leftmost edge pixel to start
  let startX = width, startY = height;
  for (let p of allEdgePixels) {
    if (p.y < startY || (p.y === startY && p.x < startX)) {
      startX = p.x;
      startY = p.y;
    }
  }

  // Use Moore neighborhood tracing, but allow "jumping" to nearby edge pixels
  // if we can't find a directly connected one - with increased jump distance
  const dirs = [[1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1], [0, 1], [1, 1]];
  const contour = [];
  let x = startX;
  let y = startY;

  // Find initial direction
  let dir = 0;
  for (let d = 0; d < 8; d++) {
    const nx = x + dirs[d][0];
    const ny = y + dirs[d][1];
    if (nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0) {
      dir = (d + 4) % 8;
      break;
    }
  }

  contour.push({ x, y });
  const visited = new Set();
  visited.add(`${x},${y}`);

  let steps = 0;
  const maxSteps = width * height * 4;
  let lastFoundX = x, lastFoundY = y;
  let consecutiveFailures = 0;
  const maxConsecutiveFailures = 100; // Allow more attempts before giving up

  while (steps < maxSteps) {
    steps++;
    let found = false;

    // First, try to find directly connected edge pixel (Moore neighborhood)
    for (let d = 0; d < 8; d++) {
      const nd = (dir + d + 6) % 8;
      const nx = x + dirs[nd][0];
      const ny = y + dirs[nd][1];

      if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
        const key = `${nx},${ny}`;
        if (edgeSet.has(key)) {
          x = nx;
          y = ny;
          dir = nd;
          lastFoundX = x;
          lastFoundY = y;
          if (!visited.has(key)) {
            contour.push({ x, y });
            visited.add(key);
          }
          found = true;
          consecutiveFailures = 0;
          break;
        }
      }
    }

    // If no directly connected edge pixel found, try to find nearest edge pixel
    // with increased jump distance to handle larger gaps between disconnected shapes
    if (!found) {
      let minDist = Infinity;
      let nearestX = -1, nearestY = -1;
      // Calculate maximum jump distance based on image size to handle larger gaps
      const maxJumpDist = Math.max(10, Math.min(width, height) / 10);

      for (let p of allEdgePixels) {
        const key = `${p.x},${p.y}`;
        if (!visited.has(key)) {
          const dist = Math.hypot(p.x - x, p.y - y);
          // Increased jump distance to capture disconnected shapes
          if (dist < minDist && dist <= maxJumpDist && dist > 0) {
            minDist = dist;
            nearestX = p.x;
            nearestY = p.y;
          }
        }
      }

      if (nearestX !== -1 && nearestY !== -1) {
        // Add intermediate points to bridge the gap
        const bridgeSteps = Math.max(2, Math.ceil(minDist));
        for (let i = 1; i <= bridgeSteps; i++) {
          const t = i / bridgeSteps;
          const bridgeX = Math.round(x + (nearestX - x) * t);
          const bridgeY = Math.round(y + (nearestY - y) * t);
          const bridgeKey = `${bridgeX},${bridgeY}`;
          if (!visited.has(bridgeKey)) {
            contour.push({ x: bridgeX, y: bridgeY });
            visited.add(bridgeKey);
          }
        }
        x = nearestX;
        y = nearestY;
        // Update direction based on movement
        const dx = x - lastFoundX;
        const dy = y - lastFoundY;
        if (Math.abs(dx) > Math.abs(dy)) {
          dir = dx > 0 ? 0 : 4;
        } else {
          dir = dy > 0 ? 6 : 2;
        }
        lastFoundX = x;
        lastFoundY = y;
        found = true;
        consecutiveFailures = 0;
      } else {
        consecutiveFailures++;
        // If we can't find any nearby unvisited pixels, check if there are any unvisited pixels at all
        if (consecutiveFailures >= maxConsecutiveFailures) {
          // Check if we've visited all edge pixels
          let hasUnvisited = false;
          for (let p of allEdgePixels) {
            if (!visited.has(`${p.x},${p.y}`)) {
              hasUnvisited = true;
              // Try to jump to this unvisited pixel even if far away
              const dist = Math.hypot(p.x - x, p.y - y);
              if (dist < minDist) {
                minDist = dist;
                nearestX = p.x;
                nearestY = p.y;
              }
            }
          }
          if (nearestX !== -1 && nearestY !== -1) {
            // Force jump to unvisited pixel
            const bridgeSteps = Math.max(2, Math.ceil(minDist));
            for (let i = 1; i <= bridgeSteps; i++) {
              const t = i / bridgeSteps;
              const bridgeX = Math.round(x + (nearestX - x) * t);
              const bridgeY = Math.round(y + (nearestY - y) * t);
              const bridgeKey = `${bridgeX},${bridgeY}`;
              if (!visited.has(bridgeKey)) {
                contour.push({ x: bridgeX, y: bridgeY });
                visited.add(bridgeKey);
              }
            }
            x = nearestX;
            y = nearestY;
            const dx = x - lastFoundX;
            const dy = y - lastFoundY;
            if (Math.abs(dx) > Math.abs(dy)) {
              dir = dx > 0 ? 0 : 4;
            } else {
              dir = dy > 0 ? 6 : 2;
            }
            lastFoundX = x;
            lastFoundY = y;
            found = true;
            consecutiveFailures = 0;
          } else if (!hasUnvisited) {
            // All pixels visited, we're done
            break;
          }
        }
      }
    }

    if (!found) {
      // If we still can't find anything, check if we've visited all pixels
      let allVisited = true;
      for (let p of allEdgePixels) {
        if (!visited.has(`${p.x},${p.y}`)) {
          allVisited = false;
          break;
        }
      }
      if (allVisited) break;
    }

    // Check if we've completed a loop and visited all edge pixels
    if (contour.length > 3 && Math.abs(x - startX) <= 2 && Math.abs(y - startY) <= 2) {
      // Verify we've visited all edge pixels
      let allVisited = true;
      for (let p of allEdgePixels) {
        if (!visited.has(`${p.x},${p.y}`)) {
          allVisited = false;
          break;
        }
      }
      if (allVisited) {
        break;
      } else {
        // Continue tracing from an unvisited pixel
        let nextStart = null;
        for (let p of allEdgePixels) {
          if (!visited.has(`${p.x},${p.y}`)) {
            nextStart = p;
            break;
          }
        }
        if (nextStart) {
          x = nextStart.x;
          y = nextStart.y;
          startX = x;
          startY = y;
          lastFoundX = x;
          lastFoundY = y;
          // Find new initial direction
          for (let d = 0; d < 8; d++) {
            const nx = x + dirs[d][0];
            const ny = y + dirs[d][1];
            if (nx < 0 || nx >= width || ny < 0 || ny >= height || grid[ny * width + nx] === 0) {
              dir = (d + 4) % 8;
              break;
            }
          }
        } else {
          break;
        }
      }
    }
  }

  // If contour is too short or doesn't capture all pixels, only use bounding box as last resort
  const visitedRatio = visited.size / allEdgePixels.length;
  if (contour.length < 3 || visitedRatio < 0.5) {
    console.warn(`Contour tracing incomplete (visited ${(visitedRatio * 100).toFixed(1)}% of edge pixels). This may indicate an image loading issue.`);
    
    // Only return bounding box if we have valid non-transparent pixels and it's not the entire image
    if (minX <= maxX && minY <= maxY && allNonTransparentPixels.length > 0) {
      const boxWidth = maxX - minX;
      const boxHeight = maxY - minY;
      
      // Only use bounding box if it doesn't cover most of the image (which suggests loading issue)
      if (boxWidth < width * 0.9 && boxHeight < height * 0.9) {
        console.warn('Using bounding box fallback - shape may be simplified');
        return [
          { x: minX, y: minY },
          { x: maxX, y: minY },
          { x: maxX, y: maxY },
          { x: minX, y: maxY }
        ];
      } else {
        console.error('Bounding box covers most of image - likely image loading issue. Returning incomplete contour.');
        // Return what we have rather than a misleading square
        return contour.length >= 3 ? contour : [];
      }
    }
  }

  return contour;
}

function smoothContour(contour, iterations = 3, expand = 0) {
  if (contour.length < 3) return contour;
  let smoothed = contour.map(p => ({ x: p.x, y: p.y }));
  const nn = smoothed.length;
  // Compute signed area to determine orientation (CCW if >0)
  let signedArea = 0;
  for (let i = 0; i < nn; i++) {
    const j = (i + 1) % nn;
    signedArea += smoothed[i].x * smoothed[j].y;
    signedArea -= smoothed[j].x * smoothed[i].y;
  }
  signedArea /= 2;
  const isCCW = signedArea > 0;
  // If expanding, first push points outward along their normals
  if (expand > 0) {
    for (let i = 0; i < nn; i++) {
      const prev = smoothed[(i - 1 + nn) % nn];
      const curr = smoothed[i];
      const next = smoothed[(i + 1 + nn) % nn];
      const dx1 = curr.x - prev.x;
      const dy1 = curr.y - prev.y;
      const len1 = Math.max(Math.sqrt(dx1 * dx1 + dy1 * dy1), 1e-6);
      const dx2 = next.x - curr.x;
      const dy2 = next.y - curr.y;
      const len2 = Math.max(Math.sqrt(dx2 * dx2 + dy2 * dy2), 1e-6);

      let nx1, ny1, nx2, ny2;
      if (isCCW) {
        // CCW: outward is right perp (dy, -dx)
        nx1 = dy1 / len1;
        ny1 = -dx1 / len1;
        nx2 = dy2 / len2;
        ny2 = -dx2 / len2;
      } else {
        // CW: outward is left perp (-dy, dx)
        nx1 = -dy1 / len1;
        ny1 = dx1 / len1;
        nx2 = -dy2 / len2;
        ny2 = dx2 / len2;
      }

      let nx = (nx1 + nx2) * 0.5;
      let ny = (ny1 + ny2) * 0.5;
      const nlen = Math.sqrt(nx * nx + ny * ny);
      if (nlen > 0) {
        nx /= nlen;
        ny /= nlen;
      }
      smoothed[i].x = curr.x + nx * expand;
      smoothed[i].y = curr.y + ny * expand;
    }
  }
  // Now smooth the (possibly expanded) contour with weighted average (less shrinkage)
  for (let iter = 0; iter < iterations; iter++) {
    let newSm = [];
    for (let i = 0; i < nn; i++) {
      const prev = smoothed[(i - 1 + nn) % nn];
      const curr = smoothed[i];
      const next = smoothed[(i + 1 + nn) % nn];
      const avgx = (prev.x + 2 * curr.x + next.x) / 4;
      const avgy = (prev.y + 2 * curr.y + next.y) / 4;
      newSm.push({ x: avgx, y: avgy });
    }
    smoothed = newSm;
  }
  return smoothed;
}

function resamplePolygon(points, targetCount) {
  const n = points.length;
  if (n === 0 || targetCount <= 0) return [];
  if (n === targetCount) {
    return points.map(p => ({ x: p.x, y: p.y }));
  }
  const lengths = new Array(n);
  let perimeter = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = points[j].x - points[i].x;
    const dy = points[j].y - points[i].y;
    const len = Math.hypot(dx, dy);
    lengths[i] = len;
    perimeter += len;
  }
  if (perimeter < 1e-6) {
    return Array.from({ length: targetCount }, () => ({ x: points[0].x, y: points[0].y }));
  }
  const step = perimeter / targetCount;
  const resampled = [];
  let segmentIndex = 0;
  let accumulated = 0;
  for (let k = 0; k < targetCount; k++) {
    const target = k * step;
    while (target > accumulated + lengths[segmentIndex]) {
      accumulated += lengths[segmentIndex];
      segmentIndex = (segmentIndex + 1) % n;
    }
    const segmentLength = lengths[segmentIndex];
    const fraction = segmentLength < 1e-6 ? 0 : (target - accumulated) / segmentLength;
    const p0 = points[segmentIndex];
    const p1 = points[(segmentIndex + 1) % n];
    resampled.push({
      x: p0.x + (p1.x - p0.x) * fraction,
      y: p0.y + (p1.y - p0.y) * fraction
    });
  }
  return resampled;
}

// Calculate curvature at a point (how much the shape bends inward/outward)
function calculateCurvature(points, i, centerX, centerY) {
  const n = points.length;
  const prev = points[(i - 1 + n) % n];
  const curr = points[i];
  const next = points[(i + 1 + n) % n];

  // Vector from prev to curr
  const v1x = curr.x - prev.x;
  const v1y = curr.y - prev.y;
  // Vector from curr to next
  const v2x = next.x - curr.x;
  const v2y = next.y - curr.y;

  // Normalize
  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);
  if (len1 < 1e-6 || len2 < 1e-6) return 0;

  const nv1x = v1x / len1;
  const nv1y = v1y / len1;
  const nv2x = v2x / len2;
  const nv2y = v2y / len2;

  // Angle between vectors
  const dot = nv1x * nv2x + nv1y * nv2y;
  const angle = Math.acos(Math.max(-1, Math.min(1, dot)));

  // Distance from center (indicates how deep an indentation is)
  const dx = curr.x - centerX;
  const dy = curr.y - centerY;
  const distFromCenter = Math.hypot(dx, dy);

  // Check if it's an inward bend (indentation)
  const toCenterX = centerX - curr.x;
  const toCenterY = centerY - curr.y;
  const perpX = -nv1y;
  const perpY = nv1x;
  const isInward = (toCenterX * perpX + toCenterY * perpY) > 0;

  // Higher curvature value for sharp inward bends (deep gaps)
  if (isInward && angle > Math.PI / 3) {
    return angle * (1 + distFromCenter * 0.01);
  }
  return angle;
}



// Function to draw contour on preview canvas with object-fit: cover style
function drawContourOnCanvas(canvasId, contour, sourceWidth, sourceHeight) {
  const canvas = document.getElementById(canvasId);
  if (!canvas || !contour || contour.length < 3) return;

  if (isDevMode()) {
    const silentCtx = canvas.getContext('2d');
    if (silentCtx) {
      silentCtx.clearRect(0, 0, canvas.width, canvas.height);
    }
    return;
  }

  const ctx = canvas.getContext('2d');
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  // Calculate bounding box of contour
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let p of contour) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  const shapeWidth = maxX - minX;
  const shapeHeight = maxY - minY;

  if (shapeWidth <= 0 || shapeHeight <= 0) return;

  // Calculate scale to fit entire shape inside canvas (no cropping)
  // Add small padding (5% on each side) to prevent edge touching
  const padding = 0.05;
  const availableWidth = canvasWidth * (1 - 2 * padding);
  const availableHeight = canvasHeight * (1 - 2 * padding);
  const scaleX = availableWidth / shapeWidth;
  const scaleY = availableHeight / shapeHeight;
  const scale = Math.min(scaleX, scaleY); // Use smaller scale to fit entirely

  // Calculate center of shape
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  // Clear canvas
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // Transform to center and scale
  ctx.save();
  ctx.translate(canvasWidth / 2, canvasHeight / 2);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);

  // Draw filled shape in black
  ctx.beginPath();
  ctx.moveTo(contour[0].x, contour[0].y);
  for (let i = 1; i < contour.length; i++) {
    ctx.lineTo(contour[i].x, contour[i].y);
  }
  ctx.closePath();
  ctx.fillStyle = '#000000';
  ctx.fill();

  ctx.restore();
}

// Main function to process image and generate shape
// This function requires callbacks/handlers from the main script to handle 3D generation
function processImage(image, tolerance, options = {}) {
  // Validate image is fully loaded and has valid dimensions
  if (!image || !image.complete) {
    console.error('Image is not complete, cannot process');
    return;
  }
  if (image.naturalWidth === 0 || image.naturalHeight === 0) {
    console.error('Image has invalid dimensions:', image.naturalWidth, image.naturalHeight);
    return;
  }

  // Verify we can actually access pixel data and the image has been decoded
  // This is a lighter check since we already validated in loadTexture
  try {
    const testCanvas = document.createElement('canvas');
    testCanvas.width = image.naturalWidth;
    testCanvas.height = image.naturalHeight;
    const testCtx = testCanvas.getContext('2d');
    
    // Clear canvas to ensure we get accurate pixel data
    testCtx.clearRect(0, 0, testCanvas.width, testCanvas.height);
    testCtx.drawImage(image, 0, 0);
    
    // Try to read pixel data - if it fails, the image was already validated earlier
    // so this might be a temporary issue, try again once
    let testData1, testData2;
    try {
      testData1 = testCtx.getImageData(0, 0, 1, 1);
    } catch (e) {
      console.warn('Cannot read pixel data from top-left, but continuing anyway:', e.message);
    }
    
    try {
      testData2 = testCtx.getImageData(Math.floor(image.naturalWidth / 2), Math.floor(image.naturalHeight / 2), 1, 1);
    } catch (e) {
      console.warn('Cannot read pixel data from center, but continuing anyway:', e.message);
    }
    
    // If we can read at least one location, proceed (image already validated in loadTexture)
    if ((!testData1 || testData1.data.length === 0) && (!testData2 || testData2.data.length === 0)) {
      console.warn('Cannot read image pixel data, but image was already validated - proceeding anyway');
      // Don't return - proceed with processing as image was already validated
    }
  } catch (e) {
    console.warn('Error accessing image pixel data in processImage, but continuing:', e.message);
    // Don't return - image was already validated in loadTexture, so proceed
  }

  // Extract options with defaults
  const paddingFraction = options.paddingFraction || 0.02;
  const smoothPercent = options.smoothPercent || 5;
  const secondSmoothPercent = options.secondSmoothPercent || 0;
  const magnetPower = options.magnetPower || 0.5;
  const centerThickness = options.centerThickness || 0.18;
  const maxSpikeSizeRatio = options.maxSpikeSizeRatio !== undefined ? options.maxSpikeSizeRatio : 0.05; // Default 5%
  const onComplete = options.onComplete || (() => { });
  const profile = getDeviceProfile();

  const originalData = buildBinaryGrid(image, tolerance);
  const origWidth = originalData.width;
  const origHeight = originalData.height;
  let origGrid = originalData.grid;
  
  // Validate that we have valid grid data
  if (!origGrid || origWidth === 0 || origHeight === 0) {
    console.error('Failed to build binary grid from image - image may not have loaded properly');
    console.log('Error: Could not process image. Please ensure the image is fully loaded and contains visible content.');
    return;
  }
  
  // Check if we have any non-transparent pixels
  let hasNonTransparentPixels = false;
  for (let i = 0; i < origGrid.length; i++) {
    if (origGrid[i] === 1) {
      hasNonTransparentPixels = true;
      break;
    }
  }
  
  if (!hasNonTransparentPixels) {
    console.error('No non-transparent pixels found in image');
    console.log('Error: Image appears to be completely transparent or failed to load properly. Please try a different image.');
    return;
  }
  
  // Apply morphological opening to fill narrow grooves and smooth small indentations
  const openingIterations = 1;
  const eroded = erodeBinary(origGrid, origWidth, origHeight, openingIterations);
  const openedGrid = dilateBinary(eroded, origWidth, origHeight, openingIterations);
  origGrid = openedGrid;
  const smallDilated = dilateBinary(origGrid, origWidth, origHeight, 2);
  let originalContour = traceContourFromGrid(smallDilated, origWidth, origHeight);
  
  if (originalContour.length < 3) {
    console.error('Unable to extract contour from image - image may not have loaded properly or is invalid');
    console.log('Error: Could not detect shape in image. Please ensure the image has visible content and try again.');
    return;
  }
  
  // Check if the contour is suspiciously like a bounding box - but be lenient
  // Some images are legitimately rectangular shapes, so only warn for extreme cases
  if (originalContour.length === 4) {
    // Check if it's a perfect rectangle covering almost the entire image (>98%)
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let p of originalContour) {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    }
    const boxWidth = maxX - minX;
    const boxHeight = maxY - minY;
    
    // Only be concerned if it covers > 98% of the image AND the contour points match corners
    // This is much more lenient - only flags extreme cases
    const isNearFullWidth = boxWidth > origWidth * 0.98;
    const isNearFullHeight = boxHeight > origHeight * 0.98;
    
    // Check if the 4 points actually form a rectangle at the image corners
    const isAtCorners = (
      (minX <= 2 && minY <= 2) &&  // top-left corner
      (maxX >= origWidth - 3 && minY <= 2) &&  // top-right corner
      (maxX >= origWidth - 3 && maxY >= origHeight - 3) &&  // bottom-right corner
      (minX <= 2 && maxY >= origHeight - 3)  // bottom-left corner
    );
    
    // Only warn (don't block) if it's clearly the entire image bounds
    if (isNearFullWidth && isNearFullHeight && isAtCorners) {
      console.warn('Contour appears to match entire image bounds - this might indicate an image loading issue, but proceeding anyway');
      // Don't return - let it process anyway since image was already validated
      // The user can see if the result is wrong and try again
    }
  }


  // Draw original contour on preview canvas
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let p of originalContour) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const shapeWidth = maxX - minX;
  const shapeHeight = maxY - minY;
  const size = Math.min(shapeWidth, shapeHeight);
  const pad = Math.round(paddingFraction * size);
  const paddedWidth = origWidth + 2 * pad;
  const paddedHeight = origHeight + 2 * pad;
  // Pad the image
  const paddedCanvas = document.createElement('canvas');
  paddedCanvas.width = paddedWidth;
  paddedCanvas.height = paddedHeight;
  const pCtx = paddedCanvas.getContext('2d');
  pCtx.clearRect(0, 0, paddedWidth, paddedHeight);
  pCtx.drawImage(image, pad, pad);

  // Upload to texture if handler provided
  if (options.uploadTexture) {
    options.uploadTexture(paddedCanvas);
  }

  // Pad the grid
  const paddedGrid = new Uint8Array(paddedWidth * paddedHeight);
  for (let y = 0; y < origHeight; y++) {
    for (let x = 0; x < origWidth; x++) {
      paddedGrid[(y + pad) * paddedWidth + (x + pad)] = origGrid[y * origWidth + x];
    }
  }
  // Dilate for padding
  const dilationIterations = Math.max(1, Math.round(pad * profile.dilationScale));
  const dilatedGrid = dilateBinary(paddedGrid, paddedWidth, paddedHeight, dilationIterations);
  // Connect components with small center lines
  connectComponents(dilatedGrid, paddedWidth, paddedHeight);
  // Trace contour
  let contour = traceContourFromGrid(dilatedGrid, paddedWidth, paddedHeight);
  if (contour.length < 3) {
    console.warn('Unable to extract padded contour');
    return;
  }
  drawContourOnCanvas('originalCanvas', contour, origWidth, origHeight);
  // Compute smoothing and resampling
  let totalLength = 0;
  const n = contour.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = contour[j].x - contour[i].x;
    const dy = contour[j].y - contour[i].y;
    totalLength += Math.sqrt(dx * dx + dy * dy);
  }
  const avgSeg = totalLength / n;
  const desiredSmoothDistance = (smoothPercent / 100) * size;
  let computedIterations = Math.max(1, Math.round((desiredSmoothDistance / avgSeg) * 4));
  computedIterations = Math.min(computedIterations, 200);
  console.log(`Shape size: ${size}px, desired smooth: ${desiredSmoothDistance}px, avg segment: ${avgSeg.toFixed(2)}px, computed iterations: ${computedIterations}`);
  contour = smoothContour(contour, computedIterations, 0);
  if (secondSmoothPercent > 0) {
    // Compute length after first smooth for second smoothing
    let totalLength2 = 0;
    const nn = contour.length;
    for (let i = 0; i < nn; i++) {
      const j = (i + 1) % nn;
      const dx = contour[j].x - contour[i].x;
      const dy = contour[j].y - contour[i].y;
      totalLength2 += Math.sqrt(dx * dx + dy * dy);
    }
    const avgSeg2 = totalLength2 / nn;
    const desiredSmoothDistance2 = (secondSmoothPercent / 100) * size;
    let computedIterations2 = Math.max(1, Math.round((desiredSmoothDistance2 / avgSeg2) * 8)); // Increased multiplier for stronger second smoothing
    computedIterations2 = Math.min(computedIterations2, 200);
    console.log(`Second smooth: desired smooth: ${desiredSmoothDistance2}px, avg segment: ${avgSeg2.toFixed(2)}px, computed iterations: ${computedIterations2}`);
    contour = smoothContour(contour, computedIterations2, 0);
  }
  // Now compute final length after all smoothing for accurate resampling
  let finalLength = 0;
  const finalN = contour.length;
  for (let i = 0; i < finalN; i++) {
    const j = (i + 1) % finalN;
    const dx = contour[j].x - contour[i].x;
    const dy = contour[j].y - contour[i].y;
    finalLength += Math.hypot(dx, dy);
  }
  const edgeSpacingDivisor = profile.edgeSpacingDivisor || 320;
  let desiredEdgeSpacing = Math.max(1.5, size / edgeSpacingDivisor);
  let targetSamples = Math.round(finalLength / desiredEdgeSpacing);
  targetSamples = Math.min(profile.maxEdgeSamples, Math.max(finalN, targetSamples));
  contour = resamplePolygon(contour, targetSamples);

  // Remove spikes, thin parts, and stick-like objects
  // contour = removeSpikesAndThinParts(contour, size, { maxSpikeSizeRatio: maxSpikeSizeRatio });

  // Draw smoothed contour on preview canvas
  drawContourOnCanvas('smoothedCanvas', contour, paddedWidth, paddedHeight);

  // Calculate center for pillow shape processing
  let centerX = 0, centerY = 0;
  for (let p of contour) {
    centerX += p.x;
    centerY += p.y;
  }
  centerX /= contour.length;
  centerY /= contour.length;

  // Create final pillow-like shape with aggressive smoothing
  let finalShape = createPillowShape(contour, centerX, centerY, size, magnetPower);

  // Resample final shape to ensure enough points for smooth 3D generation
  let finalShapeLength = 0;
  const finalShapeN = finalShape.length;
  for (let i = 0; i < finalShapeN; i++) {
    const j = (i + 1) % finalShapeN;
    const dx = finalShape[j].x - finalShape[i].x;
    const dy = finalShape[j].y - finalShape[i].y;
    finalShapeLength += Math.hypot(dx, dy);
  }
  const finalSampleDivisor = profile.finalSampleDivisor || 200;
  const finalTargetSamples = Math.max(128, Math.min(profile.finalSampleCap, Math.round(finalShapeLength / (size / finalSampleDivisor))));
  finalShape = resamplePolygon(finalShape, finalTargetSamples);

  // Apply the same smoothing as smoothedCanvas to make final shape very smooth
  // Use the same calculation method as smoothedCanvas
  let finalTotalLength = 0;
  const finalShapeN2 = finalShape.length;
  for (let i = 0; i < finalShapeN2; i++) {
    const j = (i + 1) % finalShapeN2;
    const dx = finalShape[j].x - finalShape[i].x;
    const dy = finalShape[j].y - finalShape[i].y;
    finalTotalLength += Math.sqrt(dx * dx + dy * dy);
  }
  const finalAvgSeg = finalTotalLength / finalShapeN2;
  const finalDesiredSmoothDistance = (smoothPercent / 100) * size;
  // Increase multiplier from 4 to 6 for more aggressive smoothing
  let finalComputedIterations = Math.max(1, Math.round((finalDesiredSmoothDistance / finalAvgSeg) * 6));
  finalComputedIterations = Math.min(finalComputedIterations, 300); // Increased max from 200 to 300
  console.log(`Final shape smoothing: size: ${size}px, desired smooth: ${finalDesiredSmoothDistance}px, avg segment: ${finalAvgSeg.toFixed(2)}px, computed iterations: ${finalComputedIterations}`);
  finalShape = smoothContour(finalShape, finalComputedIterations, 0);
  
  // Apply second smoothing pass if enabled (same as smoothedCanvas)
  if (secondSmoothPercent > 0) {
    let finalTotalLength2 = 0;
    const finalN2 = finalShape.length;
    for (let i = 0; i < finalN2; i++) {
      const j = (i + 1) % finalN2;
      const dx = finalShape[j].x - finalShape[i].x;
      const dy = finalShape[j].y - finalShape[i].y;
      finalTotalLength2 += Math.sqrt(dx * dx + dy * dy);
    }
    const finalAvgSeg2 = finalTotalLength2 / finalN2;
    const finalDesiredSmoothDistance2 = (secondSmoothPercent / 100) * size;
    // Increase multiplier from 8 to 12 for more aggressive second smoothing
    let finalComputedIterations2 = Math.max(1, Math.round((finalDesiredSmoothDistance2 / finalAvgSeg2) * 12));
    finalComputedIterations2 = Math.min(finalComputedIterations2, 300); // Increased max from 200 to 300
    console.log(`Final shape second smooth: desired smooth: ${finalDesiredSmoothDistance2}px, avg segment: ${finalAvgSeg2.toFixed(2)}px, computed iterations: ${finalComputedIterations2}`);
    finalShape = smoothContour(finalShape, finalComputedIterations2, 0);
  }
  
  // Apply additional final smoothing pass for extra smoothness
  let finalTotalLength3 = 0;
  const finalN3 = finalShape.length;
  for (let i = 0; i < finalN3; i++) {
    const j = (i + 1) % finalN3;
    const dx = finalShape[j].x - finalShape[i].x;
    const dy = finalShape[j].y - finalShape[i].y;
    finalTotalLength3 += Math.sqrt(dx * dx + dy * dy);
  }
  const finalAvgSeg3 = finalTotalLength3 / finalN3;
  // Use a percentage of the shape size for additional smoothing
  const additionalSmoothDistance = size * 0.03; // 3% of size for additional smoothing
  let additionalSmoothIterations = Math.max(10, Math.round((additionalSmoothDistance / finalAvgSeg3) * 4));
  additionalSmoothIterations = Math.min(additionalSmoothIterations, 150);
  console.log(`Final shape additional smooth: additional smooth: ${additionalSmoothDistance}px, avg segment: ${finalAvgSeg3.toFixed(2)}px, computed iterations: ${additionalSmoothIterations}`);
  finalShape = smoothContour(finalShape, additionalSmoothIterations, 0);

  // Draw final shape on preview canvas
  drawContourOnCanvas('finalCanvas', finalShape, paddedWidth, paddedHeight);

  // Call onComplete callback with the final shape and related data
  onComplete(finalShape, finalShape, centerThickness, paddedWidth, paddedHeight);
}
